import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Stack,
  Typography,
} from '@mui/material';

import axios, { AxiosResponse } from 'axios';
import { twoDigitCurrencyFormatter } from 'utils/helpers/currencyFormatter';
import {
  TimeTableHeadCell,
  TimeTableCell,
  // StyledFilterWrapper,
} from 'molecules/TimeLogTable/TimeLogTable.styles';
import { neutrals } from 'utils/styles/color';
// import { ExportCSVButton } from 'organisms/SalesReport/SalesReport.styles';
import { CONTRACT_TYPE } from 'utils/constants/common';
import loaderGif from 'assets/images/loaderGif.gif';
import { formatHours } from 'utils/helpers/dates';
import { StyledTableCell } from 'molecules/Table/styles';
// import { getFilenameCSVFromHeader } from 'organisms/InvoicesTable/helpers';
import {
  IAdjustmentRow,
  ITimeLog,
  ITimeLogTableProps,
} from 'molecules/TimeLogTable';

const AdjustmentTable: FC<ITimeLogTableProps> = ({ report }) => {
  const [loading, setLoading] = useState<boolean>(false);
  // const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [data, setData] = useState<IAdjustmentRow[]>([]);

  const { id, currency } = report || {};

  // const handleExportCsv = useCallback(async () => {
  //   try {
  //     setExportLoading(true);
  //     const response: AxiosResponse<string> = await axios.get(
  //       `${process.env.REACT_APP_USER_SERVICE}/project/report-timelogs`,
  //       {
  //         params: {
  //           id,
  //           export_csv: true,
  //         },
  //         headers: {
  //           'Content-Type': 'application/json',
  //           Accept: 'application/json',
  //         },
  //       }
  //     );

  //     // Create a Blob from the CSV data
  //     const blob = new Blob([response?.data], { type: 'text/csv' });

  //     const filename = getFilenameCSVFromHeader(response);
  //     const link = document.createElement('a');
  //     link.href = URL.createObjectURL(blob);
  //     link.setAttribute('download', filename);
  //     document.body.appendChild(link);
  //     link.click();
  //   } catch (e) {
  //     // eslint-disable-next-line
  //     console.error(e);
  //   } finally {
  //     setExportLoading(false);
  //   }
  // }, []);

  const getRateDetails = (row: IAdjustmentRow) => {
    if (report?.contract_type === CONTRACT_TYPE.PRORATED_FIXED_PRICE) {
      return `${twoDigitCurrencyFormatter(
        Number(row.rate_per_hour),
        currency
      )}/day`;
    }

    if (
      [
        CONTRACT_TYPE.MIXED_PRICE,
        CONTRACT_TYPE.TIME_AND_MATERIAL,
        CONTRACT_TYPE.TIME_AND_MATERIAL_AT_COST,
      ].includes(report?.contract_type)
    ) {
      return `${twoDigitCurrencyFormatter(
        Number(row.rate_per_hour),
        currency
      )}/hour`;
    }

    return '';
  };

  useEffect(() => {
    fetchData();
  }, [report.id]);

  async function fetchData() {
    setLoading(true);
    const response: AxiosResponse<ITimeLog> = await axios.get(
      `${process.env.REACT_APP_USER_SERVICE}/project/report-timelogs`,
      {
        params: { id },
      }
    );

    const sortedData = response.data.adjustment || [];

    sortedData.sort((a, b) => a.user_name.localeCompare(b.user_name));

    setData(sortedData);
    setLoading(false);
  }

  return (
    <>
      {/* <StyledFilterWrapper $isEnd>
        <ExportCSVButton
          loading={exportLoading}
          onClick={handleExportCsv}
          disabled={loading || data?.length === 0}
        >
          Download CSV
        </ExportCSVButton>
      </StyledFilterWrapper> */}
      <TableContainer sx={{ maxHeight: 380 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center' }}>
            <img src={loaderGif} alt="Data loading" />
          </Box>
        ) : (
          <Table stickyHeader aria-label="Adjustment table">
            <TableHead>
              <TableRow>
                <TimeTableHeadCell>
                  <Stack direction="column">
                    <span>Name</span>
                    <span>Rate</span>
                  </Stack>
                </TimeTableHeadCell>
                <TimeTableHeadCell>
                  <Stack direction="column">
                    <span>Snapshot</span>
                    <span>Total fee</span>
                  </Stack>
                </TimeTableHeadCell>
                <TimeTableHeadCell>
                  <Stack direction="column">
                    <span>Current</span>
                    <span>Total fee</span>
                  </Stack>
                </TimeTableHeadCell>
                <TimeTableHeadCell>
                  <Stack direction="column">
                    <span>Adjustment</span>
                    <span>Total fee</span>
                  </Stack>
                </TimeTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data?.length ? (
                <>
                  <TableRow
                    sx={{
                      '.MuiTableCell-root': { borderBottom: 'none !important' },
                    }}
                  >
                    <StyledTableCell
                      colSpan={14}
                      sx={{
                        textAlign: 'center',
                        fontSize: '0.75rem !important',
                        fontWeight: '300 !important',
                        color: `${neutrals[500]} !important`,
                      }}
                    >
                      No Adjustment data.
                    </StyledTableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      '.MuiTableCell-root': { borderBottom: 'none !important' },
                    }}
                  >
                    <StyledTableCell colSpan={14} />
                  </TableRow>
                </>
              ) : (
                data.map((row) => (
                  <TableRow key={row.user_id}>
                    <TimeTableCell>
                      <Stack direction="column">
                        <Typography
                          component="span"
                          sx={{ fontWeight: '500 !important' }}
                        >
                          {row.user_name}
                        </Typography>
                        <span>{getRateDetails(row)}</span>
                      </Stack>
                    </TimeTableCell>
                    <TimeTableCell $isBold>
                      <Stack direction="column">
                        <span>{formatHours(row.invoiced_hours ?? 0)} h</span>
                        <span>
                          {twoDigitCurrencyFormatter(
                            Number(row.snapshot_total || 0),
                            currency
                          )}
                        </span>
                      </Stack>
                    </TimeTableCell>
                    <TimeTableCell $isBold>
                      <Stack direction="column">
                        <span>{formatHours(row.current_hours ?? 0)} h</span>
                        <span>
                          {twoDigitCurrencyFormatter(
                            Number(row.current_total || 0),
                            currency
                          )}
                        </span>
                      </Stack>
                    </TimeTableCell>
                    <TimeTableCell $isBold>
                      <Stack direction="column">
                        <Typography
                          component="span"
                          sx={{ fontWeight: '600 !important' }}
                        >
                          {formatHours(row.delta_hours ?? 0)} h
                        </Typography>
                        <span>
                          {twoDigitCurrencyFormatter(
                            Number(row.adjustment_total || 0),
                            currency
                          )}
                        </span>
                      </Stack>
                    </TimeTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export default AdjustmentTable;
